import React, { useState, useEffect } from "react";
import { Check, Loader, FileUp, Database, Monitor } from "lucide-react";

const ProcessingTimeline = ({ file, stopeId, onProcessingComplete }) => {
  const [steps, setSteps] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false); // Added this line

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    setSteps([
      {
        id: 0,
        label: "Uploaded File",
        description: "File successfully transferred",
        icon: FileUp,
        status: "pending",
        visible: false,
      },
      {
        id: 1,
        label: "Converting to Potree",
        description: "Processing point cloud data",
        icon: Database,
        status: "pending",
        visible: false,
      },
      {
        id: 2,
        label: "Generating Viewer",
        description: "Preparing 3D visualization",
        icon: Monitor,
        status: "pending",
        visible: false,
      },
    ]);
  }, []);

  const processUploadResult = async () => {
    if (loading) return;
    setLoading(true); // Updated this line
    setError(null);

    try {
      if (!file || !stopeId) {
        throw new Error("Missing file or Stope ID.");
      }

      // Simulate progress updates
      setSteps((prev) =>
        prev.map((step, idx) => ({
          ...step,
          visible: true,
          status: idx === 0 ? "completed" : idx === 1 ? "current" : "pending",
        }))
      );

      await new Promise((resolve) => setTimeout(resolve, 1500));

      setSteps((prev) =>
        prev.map((step, idx) => ({
          ...step,
          status: idx <= 1 ? "completed" : idx === 2 ? "current" : "pending",
        }))
      );

      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API_BASE_URL}/upload/`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${errorText}`);
      }

      const data = await response.json();
      if (!data.file_id || !data.metadata_url || !data.volume) {
        throw new Error("Invalid server response.");
      }

      // Pass metadata_url to Potree Viewer
      onProcessingComplete(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false); // Updated this line
    }
  };

  useEffect(() => {
    if (file && stopeId) {
      setIsVisible(true);
      processUploadResult();
    }
  }, [file, stopeId]);

  const getCompletedSteps = () => {
    return steps.filter((step) => step.status === "completed").length;
  };

  const getProgressPercentage = () => {
    const completed = getCompletedSteps();
    return (completed / (steps.length - 1)) * 100;
  };

  return (
    <div className="w-full max-w-2xl mx-auto mt-8 px-4">
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="relative">
        {/* Main vertical line */}
        <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gray-200" />

        {/* Progress overlay */}
        <div
          className="absolute left-1/2 transform -translate-x-1/2 w-0.5 bg-green-500 transition-all duration-500 ease-in-out"
          style={{
            height: `${getProgressPercentage()}%`,
            top: 0,
          }}
        />

        <div className="relative space-y-16">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className="w-1/2 pr-8">
                <div className="flex justify-end">
                  <div className="text-right">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {step.label}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>

              <div
                className={`z-10 flex items-center justify-center w-12 h-12 rounded-full border-2 
                  transition-all duration-500 ease-in-out transform
                  ${
                    step.status === "completed"
                      ? "bg-green-500 border-green-500 scale-110"
                      : step.status === "current"
                      ? "bg-blue-500 border-blue-500 scale-110"
                      : "bg-white border-gray-300"
                  }`}
              >
                {step.status === "completed" ? (
                  <Check className="w-6 h-6 text-white" />
                ) : step.status === "current" ? (
                  <div className="relative">
                    <step.icon className="w-6 h-6 text-white animate-pulse" />
                    <div className="absolute inset-0">
                      <step.icon className="w-6 h-6 text-white opacity-75 animate-ping" />
                    </div>
                  </div>
                ) : (
                  <step.icon className="w-6 h-6 text-gray-400" />
                )}
              </div>

              <div className="w-1/2 pl-8">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
                    transition-all duration-300 ease-in-out
                    ${
                      step.status === "completed"
                        ? "bg-green-100 text-green-800"
                        : step.status === "current"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                >
                  {step.status === "completed" ? (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      Complete
                    </>
                  ) : step.status === "current" ? (
                    <>
                      <Loader className="w-4 h-4 mr-1 animate-spin" />
                      In Progress
                    </>
                  ) : (
                    <>
                      <step.icon className="w-4 h-4 mr-1" />
                      Pending
                    </>
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProcessingTimeline;
